// Series-shape helpers shared by the chart components (no DOM, unit-tested).

/**
 * One telemetry sample for charting. `v` is the value (null = a gap the
 * line must break across, never bridge). `lo`/`hi` are optional per-bucket
 * extremes (the min/max envelope drawn behind an averaged trace).
 */
export interface SeriesPoint {
  t: number;
  v: number | null;
  lo?: number;
  hi?: number;
}

/** Contiguous non-gap runs, ready for per-segment path building. */
export function segments(points: SeriesPoint[]): Array<Array<SeriesPoint & { v: number }>> {
  const out: Array<Array<SeriesPoint & { v: number }>> = [];
  let run: Array<SeriesPoint & { v: number }> = [];
  for (const p of points) {
    if (p.v === null || !Number.isFinite(p.v)) {
      if (run.length > 0) out.push(run);
      run = [];
    } else {
      run.push(p as SeriesPoint & { v: number });
    }
  }
  if (run.length > 0) out.push(run);
  return out;
}

export interface Extent {
  min: number;
  max: number;
}

/**
 * Value extent across several series; includes the lo/hi envelope when
 * `includeBand` so the band never clips. Null when there is no numeric data.
 */
export function seriesExtent(seriesList: SeriesPoint[][], includeBand: boolean): Extent | null {
  let min = Infinity;
  let max = -Infinity;
  for (const points of seriesList) {
    for (const p of points) {
      if (p.v === null || !Number.isFinite(p.v)) continue;
      if (p.v < min) min = p.v;
      if (p.v > max) max = p.v;
      if (includeBand) {
        if (p.lo !== undefined && Number.isFinite(p.lo) && p.lo < min) min = p.lo;
        if (p.hi !== undefined && Number.isFinite(p.hi) && p.hi > max) max = p.hi;
      }
    }
  }
  return min <= max ? { min, max } : null;
}

export interface Extremes {
  min: SeriesPoint & { v: number };
  max: SeriesPoint & { v: number };
}

/** First-occurrence min and max samples of a series (gaps skipped). */
export function findExtremes(points: SeriesPoint[]): Extremes | null {
  let min: (SeriesPoint & { v: number }) | null = null;
  let max: (SeriesPoint & { v: number }) | null = null;
  for (const p of points) {
    if (p.v === null || !Number.isFinite(p.v)) continue;
    const pv = p as SeriesPoint & { v: number };
    if (min === null || pv.v < min.v) min = pv;
    if (max === null || pv.v > max.v) max = pv;
  }
  return min !== null && max !== null ? { min, max } : null;
}

export interface LocalExtreme {
  point: SeriesPoint & { v: number };
  kind: 'max' | 'min';
}

/**
 * Every meaningful peak and trough of a series (tide highs/lows, pressure
 * turns) — direction-change candidates filtered by prominence (fraction of
 * the value range) and minimum time separation, alternating max/min so
 * noise never earns a label. Endpoints are not extremes.
 */
export function localExtremes(
  points: SeriesPoint[],
  opts: { prominence?: number; minSepFrac?: number } = {}
): LocalExtreme[] {
  const vals = points.filter(
    (p): p is SeriesPoint & { v: number } => p.v !== null && Number.isFinite(p.v)
  );
  if (vals.length < 3) return [];
  const ext = findExtremes(vals);
  if (ext === null || ext.max.v === ext.min.v) return [];
  const promAbs = (ext.max.v - ext.min.v) * (opts.prominence ?? 0.12);
  const first = vals[0]!;
  const last = vals[vals.length - 1]!;
  const sepMs = (last.t - first.t) * (opts.minSepFrac ?? 0.04);

  // Direction-change candidates (plateaus collapse to their first sample).
  const candidates: LocalExtreme[] = [];
  let prevSlope = 0;
  for (let i = 1; i < vals.length; i += 1) {
    const dv = vals[i]!.v - vals[i - 1]!.v;
    const slope = dv > 0 ? 1 : dv < 0 ? -1 : 0;
    if (slope === 0) continue;
    if (prevSlope !== 0 && slope !== prevSlope) {
      candidates.push({ point: vals[i - 1]!, kind: prevSlope > 0 ? 'max' : 'min' });
    }
    prevSlope = slope;
  }

  // Zigzag filter: alternate kinds, each swing clearing the prominence and
  // separation floors; a same-kind repeat keeps the more extreme sample.
  const kept: LocalExtreme[] = [];
  for (const c of candidates) {
    const prev = kept[kept.length - 1];
    if (prev === undefined) {
      kept.push(c);
      continue;
    }
    if (c.kind === prev.kind) {
      const better = c.kind === 'max' ? c.point.v > prev.point.v : c.point.v < prev.point.v;
      if (better) kept[kept.length - 1] = c;
      continue;
    }
    const swing = Math.abs(c.point.v - prev.point.v);
    if (swing < promAbs || c.point.t - prev.point.t < sepMs) {
      // Too small a wiggle to count as a turn — absorb it: if it pushes the
      // previous extreme further out, keep the further sample instead.
      continue;
    }
    kept.push(c);
  }
  return kept;
}

/** Index of the sample nearest in time (binary search; -1 when empty). */
export function nearestIndex(points: SeriesPoint[], t: number): number {
  const n = points.length;
  if (n === 0) return -1;
  let lo = 0;
  let hi = n - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]!.t < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(points[lo - 1]!.t - t) <= Math.abs(points[lo]!.t - t)) return lo - 1;
  return lo;
}

/**
 * The series' value at time `t`, interpolated between the samples either side
 * of it — where the chart's "now" marker sits on a forecast curve. Null when
 * `t` falls outside the series, or either neighbour is a gap: a marker must
 * never sit on a line the chart does not draw.
 */
export function valueAt(points: SeriesPoint[], t: number): number | null {
  for (let i = 0; i < points.length; i += 1) {
    const b = points[i]!;
    if (b.t < t) continue;
    if (b.v === null || !Number.isFinite(b.v)) return null;
    if (b.t === t) return b.v;
    const a = points[i - 1];
    if (a === undefined || a.v === null || !Number.isFinite(a.v)) return null;
    return a.v + ((b.v - a.v) * (t - a.t)) / (b.t - a.t);
  }
  return null;
}

/** A limit drawn on a chart: past it is above a high limit, below a low one. */
export interface Threshold {
  value: number;
  direction: 'above' | 'below';
}

/**
 * Whether a value is past a threshold. On the limit is not past, so a reading
 * sitting exactly on it is not named as over.
 */
export function pastThreshold(value: number | null, threshold: Threshold): boolean {
  if (value === null || !Number.isFinite(value)) return false;
  return threshold.direction === 'above' ? value > threshold.value : value < threshold.value;
}

/** Time extent across series (null when all empty). */
export function timeExtent(seriesList: SeriesPoint[][]): Extent | null {
  let min = Infinity;
  let max = -Infinity;
  for (const points of seriesList) {
    if (points.length === 0) continue;
    const first = points[0]!;
    const last = points[points.length - 1]!;
    if (first.t < min) min = first.t;
    if (last.t > max) max = last.t;
  }
  return min <= max ? { min, max } : null;
}
